import { Graphics } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import type { PatternRunner } from '@/patterns/emitter';
import { getTexture } from '@/assets/resolver';
import type { PatternDef } from '@/patterns/dsl';
import type { MinionKind } from '@/entities/minions';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { SummonGate } from './summon-gate';

/**
 * B14 — Admiral. A capital flagship guarded by an escort fleet. Unlike the
 * Hive's all-or-nothing shield, the escorts form a SCREEN: every living
 * escort holds one shield arc around the bridge and soaks most of each hit,
 * so damage leaks through in proportion to the fleet you have shot down
 * (docs/02 B2). Clear the wing to crack the bridge open for a window, then a
 * fresh, larger wing deploys. Fires wide BROADSIDE salvos the whole time.
 * Signatures: escort shield gate (always), broadside ring (P1/P2), line-
 * abreast bolt wall (P2), bridge broadside spray while exposed.
 */
export const ADMIRAL: BossDef = {
  id: 'admiral',
  nameKey: 'boss.admiral',
  tier: 4,
  minRoom: 2,
  baseHp: 1150,
  radius: 80,
  bodyKey: 'boss/admiral/body',
  movement: 'strafe',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/bolt', count: 12, interval: 3.2,
          spinDeg: 14, speed: 130, radius: 12, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.5,
      speedMul: 1.2,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/bolt', count: 14, interval: 3.0,
          spinDeg: 17, speed: 140, radius: 12, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/ring', count: 3, spreadDeg: 22,
          interval: 2.8, telegraph: 0.4, speed: 250, radius: 12, damage: 2,
          unlockTier: 1,
        },
        {
          // Line abreast: a broadside WALL of bolts with a gap (B3 wall)
          type: 'wall', bullet: 'bullet/enemy/bolt', count: 11, spacing: 52, hole: 2,
          interval: 3.6, telegraph: 0.5, speed: 175, radius: 11, damage: 1,
        },
      ],
    },
  ],
  evolve: [
    {
      // Flak picket: the bridge guns snap a quick double shot
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/bolt', count: 2, spreadDeg: 10,
        interval: 3.4, telegraph: 0.45, burst: 2, burstGap: 0.25,
        speed: 270, radius: 10, damage: 1,
      },
    },
    {
      // Point defence: a slow twin-arm bolt screen once the hull is hurt
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/bolt', arms: 2, interval: 0.22,
        angularSpeed: 55, speed: 140, radius: 10, damage: 1,
      },
    },
  ],
};

/**
 * Broadside spray fired only while the bridge is exposed (punish window) —
 * tier gated and ramped like every other attack (X6).
 */
const EXPOSED_PATTERNS: ReadonlyArray<PatternDef> = [
  {
    type: 'radial', bullet: 'bullet/enemy/bolt', count: 18, interval: 1.7,
    spinDeg: 21, speed: 150, radius: 12, damage: 1,
  },
  {
    type: 'aimed', bullet: 'bullet/enemy/ring', count: 3, spreadDeg: 18,
    interval: 2.2, telegraph: 0.4, unlockTier: 2,
    speed: 245, radius: 11, damage: 2,
  },
];

const EXPOSED_WINDOW = 5;
const BASE_FLEET = 3;
const FLEET_GROWTH = 1;
const MAX_FLEET = 7;
/** Escort screen: a full wing soaks this share of each hit (arcs show it) */
const SCREEN = { soak: 0.95, ringPad: 20, arcGap: 0.18, color: 0x8fb4d8, core: 0xeaf2ff };

export class AdmiralBoss extends Boss {
  private wing = 0;
  private exposedTimer = 0;
  /** Escort shield — drops when the wing dies (or on the stall failsafe) */
  private readonly gate = new SummonGate();
  private exposedRunners: PatternRunner[] = [];
  private started = false;
  /** Escorts in the current wing (for the screen share + arcs) */
  private wingSize = 1;
  private escorts = 0;
  private readonly screen = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(ADMIRAL, level, x, y, homeX, homeY);
    this.allowReinforce = false; // the fleet is the gate
    this.view.addChild(this.screen);
  }

  /** Share of each hit that reaches the bridge through the escort screen. */
  private get leak(): number {
    if (!this.shielded) return 1;
    return 1 - SCREEN.soak * Math.min(1, this.escorts / this.wingSize);
  }

  private get shielded(): boolean {
    return this.gate.locked;
  }

  override get isVulnerable(): boolean {
    return super.isVulnerable && this.leak > 0;
  }

  private deployWing(): void {
    this.wing++;
    const count = Math.min(MAX_FLEET, BASE_FLEET + (this.wing - 1) * FLEET_GROWTH);
    // Escort fighters: fast dashers screen the flagship; an elite from wave 2
    const kind: MinionKind = this.wing % 2 === 0 ? 'dasher' : 'drone';
    this.spawnMinions?.(kind, count);
    const elite = this.phaseIndex >= 1;
    if (elite) this.spawnMinions?.('elite', 1);
    this.wingSize = this.escorts = count + (elite ? 1 : 0);
    this.gate.raise();
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;

    if (!this.started) {
      this.started = true;
      this.deployWing();
    }

    this.escorts = this.minionCount?.() ?? 0;
    if (this.gate.update(dt, this.escorts)) {
      this.exposedTimer = EXPOSED_WINDOW;
      this.exposedRunners = this.makeRunners(EXPOSED_PATTERNS);
    } else if (!this.shielded) {
      this.exposedTimer -= dt;
      for (const r of this.exposedRunners) {
        r.update(dt, this.x, this.y, px, py, bullets, telegraphs);
      }
      if (this.exposedTimer <= 0) this.deployWing();
    }

    super.update(dt, px, py, bullets, telegraphs);
  }

  /** The escort screen soaks its share of every hit. */
  override takeDamage(amount: number): boolean {
    return super.takeDamage(amount * this.leak);
  }

  override render(alpha: number): void {
    super.render(alpha);
    // Bridge dims when shielded, glows gold when exposed
    this.body.texture = getTexture('boss/admiral/body', this.shielded ? 0 : 1);
    // One shield arc per living escort — the screen thins as they fall
    const g = this.screen;
    g.clear();
    if (!this.shielded) return;
    const r = this.def.radius + SCREEN.ringPad;
    const span = (Math.PI * 2) / this.wingSize;
    for (let i = 0; i < Math.min(this.escorts, this.wingSize); i++) {
      const a = i * span - Math.PI / 2;
      g.arc(0, 0, r, a + SCREEN.arcGap, a + span - SCREEN.arcGap)
        .stroke({ color: SCREEN.color, width: 6, alpha: 0.75, cap: 'round' });
      g.arc(0, 0, r, a + SCREEN.arcGap, a + span - SCREEN.arcGap)
        .stroke({ color: SCREEN.core, width: 1.5, alpha: 0.6 });
    }
  }
}
